// frontend/src/components/layout/ShopLayout.jsx
import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationAPI } from '../../services/api';

const ShopLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);

  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Modal state
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  // ============================================
  // NOTIFICATION FUNCTIONS
  // ============================================
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await notificationAPI.getNotifications({ limit: 20 });
      if (response.data.success) {
        setNotifications(response.data.data.notifications || []);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationAPI.getUnreadCount();
      if (response.data.success) {
        setUnreadCount(response.data.data.count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await notificationAPI.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await notificationAPI.deleteNotification(notificationId);
      const deletedNotif = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (deletedNotif && !deletedNotif.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const clearReadNotifications = async () => {
    try {
      const readNotifications = notifications.filter(n => n.read);
      for (const notif of readNotifications) {
        await notificationAPI.deleteNotification(notif.id);
      }
      setNotifications(prev => prev.filter(n => !n.read));
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  const openNotificationDetail = (notif) => {
    if (!notif.read) {
      markAsRead(notif.id);
    }
    setSelectedNotification(notif);
    setShowNotificationModal(true);
    setNotificationOpen(false);
  };

  // Notification helpers
  const getNotificationIcon = (type) => {
    const icons = {
      'cod_payment': '💰',
      'cod_payment_sent': '💸',
      'system_announcement': '📢',
      'announcement': '📢',
      'info': 'ℹ️',
      'delivery_status': '🚚'
    };
    return icons[type] || '🔔';
  };

  const getNotificationTypeLabel = (type) => {
    const labels = {
      'cod_payment': 'COD төлбөр',
      'cod_payment_sent': 'Төлбөр илгээсэн',
      'system_announcement': 'Системийн мэдэгдэл',
      'announcement': 'Мэдэгдэл',
      'info': 'Мэдээлэл',
      'delivery_status': 'Хүргэлтийн төлөв'
    };
    return labels[type] || 'Мэдэгдэл';
  };

  const getNotificationColor = (type) => {
    const colors = {
      'cod_payment': 'bg-emerald-500',
      'cod_payment_sent': 'bg-green-500',
      'system_announcement': 'bg-indigo-500',
      'announcement': 'bg-blue-500',
      'info': 'bg-gray-500',
      'delivery_status': 'bg-purple-500'
    };
    return colors[type] || 'bg-blue-500';
  };

  const timeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Дөнгөж сая';
    if (seconds < 3600) return Math.floor(seconds / 60) + ' мин өмнө';
    if (seconds < 86400) return Math.floor(seconds / 3600) + ' цаг өмнө';
    if (seconds < 604800) return Math.floor(seconds / 86400) + ' өдөр өмнө';
    return date.toLocaleDateString('mn-MN');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('mn-MN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fetch on mount and interval
  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
    
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchUnreadCount]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (notificationOpen) {
      fetchNotifications();
    }
  }, [notificationOpen, fetchNotifications]);

  // ============================================
  // MENU ITEMS
  // ============================================
  const menuItems = [
    { icon: '📊', label: 'Хянах самбар', path: '/shop/dashboard' },
    { icon: '🚚', label: 'Хүргэлт', path: '/shop/deliveries' },
    { icon: '📦', label: 'Барааны удирдлага', path: '/shop/products' },
    { icon: '💰', label: 'Төлбөр тооцоо', path: '/shop/settlement' },
    { icon: '💵', label: 'COD төлбөр', path: '/shop/cod-payments' },
    { icon: '📈', label: 'Тайлан', path: '/shop/reports' },
    { icon: '⚙️', label: 'Тохиргоо', path: '/shop/settings' }
  ];

  // ============================================
  // HANDLERS
  // ============================================
  const handleLogout = () => {
    if (confirm('Гарахдаа итгэлтэй байна уу?')) {
      logout();
      navigate('/login');
    }
  };

  const getPageTitle = () => {
    const pathTitles = {
      '/shop/dashboard': 'Хянах самбар',
      '/shop/deliveries': 'Хүргэлт',
      '/shop/products': 'Бараа',
      '/shop/settlement': 'Төлбөр тооцоо',
      '/shop/cod-payments': 'COD төлбөр',
      '/shop/reports': 'Тайлан',
      '/shop/settings': 'Тохиргоо'
    };
    
    if (location.pathname.includes('/deliveries/') && location.pathname !== '/shop/deliveries') {
      return 'Хүргэлтийн дэлгэрэнгүй';
    }
    
    if (location.pathname.includes('/products/') && location.pathname !== '/shop/products') {
      return 'Барааны дэлгэрэнгүй';
    }
    
    return pathTitles[location.pathname] || 'Онлайн Шоп';
  };

  const getPageSubtitle = () => {
    const subtitles = {
      '/shop/dashboard': 'Шопын ерөнхий мэдээлэл',
      '/shop/deliveries': 'Хүргэлтийн удирдлага',
      '/shop/products': 'Барааны удирдлага',
      '/shop/settlement': 'Хүргэлтийн төлбөр тооцоо',
      '/shop/cod-payments': 'COD төлбөрийн удирдлага',
      '/shop/reports': 'Борлуулалтын тайлан',
      '/shop/settings': 'Системийн тохиргоо'
    };
    return subtitles[location.pathname] || '';
  };

  const shopName = user?.shop?.shop_name || 'Онлайн Шоп';
  const userName = user?.full_name || user?.username || 'Хэрэглэгч';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex h-screen overflow-hidden">
        {/* ============================================ */}
        {/* SIDEBAR */}
        {/* ============================================ */}
        <aside 
          className={`
            fixed inset-y-0 left-0 z-50 
            bg-white border-r border-gray-200 
            transition-all duration-300
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'}
          `}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="flex-shrink-0 border-b border-gray-200 p-4">
              <button 
                onClick={() => setMobileMenuOpen(false)} 
                className="lg:hidden absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              {!sidebarCollapsed ? (
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">🛍️</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className="text-sm font-bold text-gray-900 leading-tight mb-1 break-words">{shopName}</h1>
                    <p className="text-xs text-gray-500 font-medium">Админ панел</p>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">🛍️</span>
                  </div>
                </div>
              )}
            </div>

            {/* Menu */}
            <nav className="flex-1 px-3 py-4 overflow-y-auto">
              <div className="space-y-1">
                {menuItems.map((item, index) => {
                  const isActive = location.pathname === item.path || 
                                   (item.path === '/shop/deliveries' && location.pathname.startsWith('/shop/deliveries/')) ||
                                   (item.path === '/shop/products' && location.pathname.startsWith('/shop/products/'));
                  
                  return (
                    <Link 
                      key={index} 
                      to={item.path} 
                      onClick={() => setMobileMenuOpen(false)} 
                      className={`flex items-center gap-3 px-3 py-3 rounded-lg transition group ${
                        isActive ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:bg-gray-50'
                      }`} 
                      title={sidebarCollapsed ? item.label : ''}
                    >
                      <span className="text-xl flex-shrink-0">{item.icon}</span>
                      {!sidebarCollapsed && (
                        <span className={`text-sm font-medium ${isActive ? 'font-semibold' : ''}`}>
                          {item.label}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </nav>

            {/* User Section */}
            <div className="flex-shrink-0 p-3 border-t border-gray-200">
              {!sidebarCollapsed ? (
                <>
                  <div className="flex items-center gap-3 px-3 py-3 bg-gray-50 rounded-lg mb-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-base font-semibold text-primary-600">{userName.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{userName}</p>
                      <p className="text-xs text-gray-500">Шопын админ</p>
                    </div>
                  </div>
                  <button 
                    onClick={handleLogout} 
                    className="w-full flex items-center justify-center gap-2 px-3 py-3 text-red-600 hover:bg-red-50 rounded-lg transition"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span className="text-sm font-medium">Гарах</span>
                  </button>
                </>
              ) : (
                <>
                  <div className="flex justify-center mb-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary-600">{userName.charAt(0)}</span>
                    </div>
                  </div>
                  <button 
                    onClick={handleLogout} 
                    className="w-full flex items-center justify-center px-2 py-3 text-red-600 hover:bg-red-50 rounded-lg transition" 
                    title="Гарах"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>
        </aside>

        {/* ============================================ */}
        {/* MAIN CONTENT AREA */}
        {/* ============================================ */}
        <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
          {/* HEADER */}
          <header className="flex-shrink-0 bg-white border-b border-gray-200 px-4 lg:px-6 h-20 flex items-center justify-between sticky top-0 z-40">
            <div className="flex items-center gap-4 flex-1">
              <button 
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)} 
                className="hidden lg:flex w-10 h-10 items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <button 
                onClick={() => setMobileMenuOpen(true)} 
                className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{getPageTitle()}</h1>
                {getPageSubtitle() && (
                  <p className="text-sm text-gray-500 mt-0.5">{getPageSubtitle()}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                <span className="text-sm">📅</span>
                <span className="text-sm font-medium text-gray-700">
                  {new Date().toLocaleDateString('mn-MN', { month: 'long', day: 'numeric' })}
                </span>
              </div>

              {/* ========================================== */}
              {/* NOTIFICATION BELL */}
              {/* ========================================== */}
              <div className="relative">
                <button 
                  onClick={() => setNotificationOpen(!notificationOpen)} 
                  className="relative w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {notificationOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setNotificationOpen(false)}></div>
                    <div className="absolute right-0 mt-2 w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                      {/* Header */}
                      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-white font-semibold text-lg">Мэдэгдэл</h3>
                              <p className="text-white/70 text-xs">
                                {unreadCount > 0 ? `${unreadCount} шинэ мэдэгдэл` : 'Шинэ мэдэгдэл байхгүй'}
                              </p>
                            </div>
                          </div>
                          {unreadCount > 0 && (
                            <button 
                              onClick={markAllAsRead}
                              className="text-white/80 hover:text-white text-xs font-medium bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full transition"
                            >
                              Бүгдийг уншсан
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Notification List */}
                      <div className="max-h-[400px] overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="py-12 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                              </svg>
                            </div>
                            <p className="text-gray-500 font-medium">Мэдэгдэл байхгүй</p>
                            <p className="text-gray-400 text-sm mt-1">Шинэ мэдэгдэл ирэхэд энд харагдана</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-100">
                            {notifications.map((notif) => (
                              <div 
                                key={notif.id}
                                className={`group relative hover:bg-gray-50 transition cursor-pointer ${!notif.read ? 'bg-emerald-50/50' : ''}`}
                              >
                                <div 
                                  onClick={() => openNotificationDetail(notif)}
                                  className="p-4 flex gap-3"
                                >
                                  {/* Icon */}
                                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${getNotificationColor(notif.type)}`}>
                                    <span className="text-xl">{getNotificationIcon(notif.type)}</span>
                                  </div>
                                  
                                  {/* Content */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                            !notif.read ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                                          }`}>
                                            {getNotificationTypeLabel(notif.type)}
                                          </span>
                                          {!notif.read && (
                                            <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                                          )}
                                        </div>
                                        <h4 className="text-sm font-semibold text-gray-900 truncate">
                                          {notif.title}
                                        </h4>
                                        <p className="text-sm text-gray-600 line-clamp-2 mt-0.5">
                                          {notif.message}
                                        </p>
                                        <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                          {timeAgo(notif.created_at)}
                                        </p>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Delete button */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteNotification(notif.id);
                                    }}
                                    className="opacity-0 group-hover:opacity-100 absolute top-3 right-3 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                                    title="Устгах"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      {notifications.length > 0 && (
                        <div className="p-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                          {notifications.some(n => n.read) && (
                            <button 
                              onClick={clearReadNotifications}
                              className="text-xs text-gray-500 hover:text-red-600 font-medium flex items-center gap-1.5 px-3 py-1.5 hover:bg-white rounded-lg transition"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Уншсаныг устгах
                            </button>
                          )}
                          <div className="ml-auto text-xs text-gray-400">
                            Нийт {notifications.length} мэдэгдэл
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* User Menu */}
              <div className="relative">
                <button 
                  onClick={() => setUserMenuOpen(!userMenuOpen)} 
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary-600">{userName.charAt(0)}</span>
                  </div>
                  <span className="hidden md:block text-sm font-medium text-gray-700">{userName}</span>
                  <svg className="hidden md:block w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)}></div>
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 z-50">
                      <div className="p-4 border-b border-gray-200">
                        <p className="text-sm font-semibold text-gray-900">{userName}</p>
                        <p className="text-xs text-gray-500">Шопын админ</p>
                      </div>
                      <div className="p-2">
                        <button 
                          onClick={() => { setUserMenuOpen(false); navigate('/shop/settings'); }} 
                          className="w-full flex items-center gap-3 px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-lg transition"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-sm font-medium">Тохиргоо</span>
                        </button>
                      </div>
                      <div className="p-2 border-t border-gray-200">
                        <button 
                          onClick={() => { setUserMenuOpen(false); handleLogout(); }} 
                          className="w-full flex items-center gap-3 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          <span className="text-sm font-medium">Гарах</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </header>

          {/* MAIN CONTENT */}
          <main className="flex-1 overflow-x-hidden overflow-y-auto p-4 lg:p-6">
            <div className="w-full max-w-full">
              {children}
            </div>
          </main>

          {/* FOOTER */}
          <footer className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-gray-600">
              <p>© 2025 <span className="font-semibold text-gray-900">Pingo Delivery</span>. Бүх эрх хуулиар хамгаалагдсан.</p>
              <div className="flex items-center gap-4">
                <a href="#" className="hover:text-primary-600 transition">Тусламж</a>
                <a href="#" className="hover:text-primary-600 transition">Нууцлал</a>
                <a href="#" className="hover:text-primary-600 transition">Холбоо барих</a>
              </div>
            </div>
          </footer>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" 
          onClick={() => setMobileMenuOpen(false)}
        ></div>
      )}

      {/* ========================================== */}
      {/* NOTIFICATION DETAIL MODAL */}
      {/* ========================================== */}
      {showNotificationModal && selectedNotification && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Overlay */}
            <div 
              className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
              onClick={() => setShowNotificationModal(false)}
            ></div>

            {/* Modal */}
            <div className="relative inline-block w-full max-w-lg bg-white rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8">
              {/* Header */}
              <div className={`px-6 py-6 ${
                selectedNotification.type === 'cod_payment' || selectedNotification.type === 'cod_payment_sent'
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-500' 
                  : selectedNotification.type === 'system_announcement' || selectedNotification.type === 'announcement'
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-500'
                  : 'bg-gradient-to-r from-blue-500 to-cyan-500'
              }`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center shadow-lg">
                      <span className="text-4xl">{getNotificationIcon(selectedNotification.type)}</span>
                    </div>
                    <div>
                      <span className="inline-block px-3 py-1 bg-white/25 text-white text-xs font-semibold rounded-full mb-2">
                        {getNotificationTypeLabel(selectedNotification.type)}
                      </span>
                      <h3 className="text-xl font-bold text-white leading-tight">
                        {selectedNotification.title}
                      </h3>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowNotificationModal(false)}
                    className="w-10 h-10 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-6">
                <div className="bg-gray-50 rounded-2xl p-5">
                  <p className="text-gray-700 text-base leading-relaxed whitespace-pre-wrap">
                    {selectedNotification.message}
                  </p>
                </div>

                {/* Meta Info */}
                <div className="mt-5 flex items-center gap-3 text-sm text-gray-500">
                  <div className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{formatDate(selectedNotification.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between gap-3">
                <button
                  onClick={() => {
                    deleteNotification(selectedNotification.id);
                    setShowNotificationModal(false);
                  }}
                  className="px-5 py-2.5 text-sm font-medium text-red-600 hover:text-white hover:bg-red-500 border border-red-200 hover:border-red-500 rounded-xl transition flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Устгах
                </button>
                <button
                  onClick={() => setShowNotificationModal(false)}
                  className="px-8 py-2.5 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 text-white text-sm font-semibold rounded-xl transition shadow-lg"
                >
                  Хаах
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopLayout;